// ─── Types ───────────────────────────────────────────────────────────────────

export interface CountryCode {
  code: string;
  flag: string;
  label: string;
  iso: string;
  focus?: boolean;
  blocked?: boolean;
}

export type ScreenKey =
  | "calendar"
  | "calendar_booked"
  | "video_focus"
  | "video_nonfocus"
  | "referral"
  | "waitlist"
  | "blocked"
  | "existing_client";

export interface FormState {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  countryCode: string;
  countryIso: string;
  phone: string;
  suppliers: string[];
  otherSystem: string | undefined;
  newsletter: boolean;
}

// ─── LeadFacts (DemoForm) ─────────────────────────────────────────────────────

export type LeadFacts = {
  countryTier: "focus" | "semi_focus" | "non_focus" | "blocked";
  supplierMatch: "supported" | "unsupported" | "unknown";
  emailQuality: "work" | "free" | "generic";
  isExistingCustomer: boolean;
  companyType: "scaffolding_contractor" | "other";
};

// ─── PricingFacts (PricingForm) ───────────────────────────────────────────────

export type PricingFacts = {
  needType: "quotation" | "planning" | "engineering" | "all";
  volumeTier: "under_20" | "20_to_100" | "100_to_300" | "over_300";
  teamSize: "solo" | "small" | "medium" | "large";
  contactMethod: "phone" | "email";
  emailQuality: "work" | "free" | "generic" | null;
  enterpriseTrigger: boolean;
};

// ─── Routing sets ─────────────────────────────────────────────────────────────

export const BLOCKED_COUNTRIES = new Set(["IR", "IL", "RU"]);

export const KNOWN_CLIENT_DOMAINS = new Set(["xervon.com", "altrad.com"]);

export const FOCUS_COUNTRIES = new Set([
  "SE",
  "NO",
  "DK",
  "FI",
  "DE",
  "AT",
  "CH",
  "NL",
  "BE"
]);

export const SEMI_FOCUS_COUNTRIES = new Set(["AU", "US"]);

export const DACH = new Set(["DE", "AT", "CH"]);

export const FREE_DOMAIN_NAMES = new Set([
  "gmail",
  "hotmail",
  "outlook",
  "icloud",
  "yahoo",
  "googlemail",
  "protonmail",
  "live",
  "msn",
  "me",
  "aol",
  "mac",
  "hey",
  "fastmail",
  "zoho",
  "yandex",
  "gmx",
  "web",
  "mail",
  "inbox",
  "tutanota",
  "mailfence"
]);

export const GENERIC_PREFIXES = new Set([
  "info",
  "admin",
  "office",
  "sales",
  "contact",
  "post",
  "procurement",
  "reception",
  "booking",
  "orders",
  "accounts",
  "service",
  "estimate",
  "quote",
  "quotes",
  "tender",
  "tenders",
  "general",
  "help",
  "billing",
  "invoice",
  "support",
  "enquiries"
]);

// ─── Suppliers ────────────────────────────────────────────────────────────────

const SUPPORTED_LIST = [
  "Layher",
  "MJ Gerüst",
  "Scafom-rux",
  "AT-PAC",
  "Haki",
  "Plettac SL 70/SL 100",
  "Plettac Perfect Contur",
  "Solideq",
  "Plus8",
  "Alustar",
  "Super9",
  "STEPUP"
] as const;

const UNSUPPORTED_LIST = [
  "Alfix",
  "Tobler",
  "Tube & fitting",
  "Kwikstage",
  "SGB",
  "Catari",
  "CETA Spa",
  "Hünnebeck Bosta",
  "Condor",
  "DOKA",
  "Euroscaffold",
  "ULMA Construction",
  "ASC",
  "Landskroon",
  "Altrex",
  "Trebex",
  "Fortal",
  "Assco Futuro",
  "Assco Quadro",
  "PERI Group"
] as const;

export const SUPPLIERS = [...SUPPORTED_LIST, ...UNSUPPORTED_LIST];
export const SUPPORTED_SYSTEMS = new Set<string>(SUPPORTED_LIST);
export const UNSUPPORTED_SYSTEMS = new Set<string>(UNSUPPORTED_LIST);

// ─── Country codes ────────────────────────────────────────────────────────────

export const COUNTRY_CODES: CountryCode[] = [
  // Focus + semi-focus (pinned top)
  { code: "+46", flag: "🇸🇪", label: "Sweden", iso: "SE", focus: true },
  { code: "+47", flag: "🇳🇴", label: "Norway", iso: "NO", focus: true },
  { code: "+45", flag: "🇩🇰", label: "Denmark", iso: "DK", focus: true },
  { code: "+358", flag: "🇫🇮", label: "Finland", iso: "FI", focus: true },
  { code: "+49", flag: "🇩🇪", label: "Germany", iso: "DE", focus: true },
  { code: "+43", flag: "🇦🇹", label: "Austria", iso: "AT", focus: true },
  { code: "+41", flag: "🇨🇭", label: "Switzerland", iso: "CH", focus: true },
  { code: "+31", flag: "🇳🇱", label: "Netherlands", iso: "NL", focus: true },
  { code: "+32", flag: "🇧🇪", label: "Belgium", iso: "BE", focus: true },
  { code: "+61", flag: "🇦🇺", label: "Australia", iso: "AU", focus: true },
  { code: "+1", flag: "🇺🇸", label: "USA", iso: "US", focus: true },
  // Rest of world (alphabetical)
  { code: "+93", flag: "🇦🇫", label: "Afghanistan", iso: "AF" },
  { code: "+355", flag: "🇦🇱", label: "Albania", iso: "AL" },
  { code: "+213", flag: "🇩🇿", label: "Algeria", iso: "DZ" },
  { code: "+54", flag: "🇦🇷", label: "Argentina", iso: "AR" },
  { code: "+374", flag: "🇦🇲", label: "Armenia", iso: "AM" },
  { code: "+994", flag: "🇦🇿", label: "Azerbaijan", iso: "AZ" },
  { code: "+973", flag: "🇧🇭", label: "Bahrain", iso: "BH" },
  { code: "+880", flag: "🇧🇩", label: "Bangladesh", iso: "BD" },
  { code: "+375", flag: "🇧🇾", label: "Belarus", iso: "BY" },
  { code: "+591", flag: "🇧🇴", label: "Bolivia", iso: "BO" },
  { code: "+387", flag: "🇧🇦", label: "Bosnia & Herzegovina", iso: "BA" },
  { code: "+55", flag: "🇧🇷", label: "Brazil", iso: "BR" },
  { code: "+359", flag: "🇧🇬", label: "Bulgaria", iso: "BG" },
  { code: "+1", flag: "🇨🇦", label: "Canada", iso: "CA" },
  { code: "+56", flag: "🇨🇱", label: "Chile", iso: "CL" },
  { code: "+86", flag: "🇨🇳", label: "China", iso: "CN" },
  { code: "+57", flag: "🇨🇴", label: "Colombia", iso: "CO" },
  { code: "+506", flag: "🇨🇷", label: "Costa Rica", iso: "CR" },
  { code: "+385", flag: "🇭🇷", label: "Croatia", iso: "HR" },
  { code: "+357", flag: "🇨🇾", label: "Cyprus", iso: "CY" },
  { code: "+420", flag: "🇨🇿", label: "Czech Republic", iso: "CZ" },
  { code: "+20", flag: "🇪🇬", label: "Egypt", iso: "EG" },
  { code: "+372", flag: "🇪🇪", label: "Estonia", iso: "EE" },
  { code: "+251", flag: "🇪🇹", label: "Ethiopia", iso: "ET" },
  { code: "+33", flag: "🇫🇷", label: "France", iso: "FR" },
  { code: "+995", flag: "🇬🇪", label: "Georgia", iso: "GE" },
  { code: "+233", flag: "🇬🇭", label: "Ghana", iso: "GH" },
  { code: "+30", flag: "🇬🇷", label: "Greece", iso: "GR" },
  { code: "+36", flag: "🇭🇺", label: "Hungary", iso: "HU" },
  { code: "+354", flag: "🇮🇸", label: "Iceland", iso: "IS" },
  { code: "+91", flag: "🇮🇳", label: "India", iso: "IN" },
  { code: "+62", flag: "🇮🇩", label: "Indonesia", iso: "ID" },
  { code: "+98", flag: "🇮🇷", label: "Iran", iso: "IR", blocked: true },
  { code: "+964", flag: "🇮🇶", label: "Iraq", iso: "IQ" },
  { code: "+353", flag: "🇮🇪", label: "Ireland", iso: "IE" },
  { code: "+972", flag: "🇮🇱", label: "Israel", iso: "IL", blocked: true },
  { code: "+39", flag: "🇮🇹", label: "Italy", iso: "IT" },
  { code: "+81", flag: "🇯🇵", label: "Japan", iso: "JP" },
  { code: "+962", flag: "🇯🇴", label: "Jordan", iso: "JO" },
  { code: "+7", flag: "🇰🇿", label: "Kazakhstan", iso: "KZ" },
  { code: "+254", flag: "🇰🇪", label: "Kenya", iso: "KE" },
  { code: "+965", flag: "🇰🇼", label: "Kuwait", iso: "KW" },
  { code: "+371", flag: "🇱🇻", label: "Latvia", iso: "LV" },
  { code: "+961", flag: "🇱🇧", label: "Lebanon", iso: "LB" },
  { code: "+218", flag: "🇱🇾", label: "Libya", iso: "LY" },
  { code: "+370", flag: "🇱🇹", label: "Lithuania", iso: "LT" },
  { code: "+352", flag: "🇱🇺", label: "Luxembourg", iso: "LU" },
  { code: "+60", flag: "🇲🇾", label: "Malaysia", iso: "MY" },
  { code: "+356", flag: "🇲🇹", label: "Malta", iso: "MT" },
  { code: "+52", flag: "🇲🇽", label: "Mexico", iso: "MX" },
  { code: "+373", flag: "🇲🇩", label: "Moldova", iso: "MD" },
  { code: "+212", flag: "🇲🇦", label: "Morocco", iso: "MA" },
  { code: "+95", flag: "🇲🇲", label: "Myanmar", iso: "MM" },
  { code: "+977", flag: "🇳🇵", label: "Nepal", iso: "NP" },
  { code: "+64", flag: "🇳🇿", label: "New Zealand", iso: "NZ" },
  { code: "+234", flag: "🇳🇬", label: "Nigeria", iso: "NG" },
  { code: "+389", flag: "🇲🇰", label: "North Macedonia", iso: "MK" },
  { code: "+968", flag: "🇴🇲", label: "Oman", iso: "OM" },
  { code: "+92", flag: "🇵🇰", label: "Pakistan", iso: "PK" },
  { code: "+507", flag: "🇵🇦", label: "Panama", iso: "PA" },
  { code: "+51", flag: "🇵🇪", label: "Peru", iso: "PE" },
  { code: "+63", flag: "🇵🇭", label: "Philippines", iso: "PH" },
  { code: "+48", flag: "🇵🇱", label: "Poland", iso: "PL" },
  { code: "+351", flag: "🇵🇹", label: "Portugal", iso: "PT" },
  { code: "+974", flag: "🇶🇦", label: "Qatar", iso: "QA" },
  { code: "+40", flag: "🇷🇴", label: "Romania", iso: "RO" },
  { code: "+7", flag: "🇷🇺", label: "Russia", iso: "RU", blocked: true },
  { code: "+966", flag: "🇸🇦", label: "Saudi Arabia", iso: "SA" },
  { code: "+381", flag: "🇷🇸", label: "Serbia", iso: "RS" },
  { code: "+65", flag: "🇸🇬", label: "Singapore", iso: "SG" },
  { code: "+421", flag: "🇸🇰", label: "Slovakia", iso: "SK" },
  { code: "+386", flag: "🇸🇮", label: "Slovenia", iso: "SI" },
  { code: "+252", flag: "🇸🇴", label: "Somalia", iso: "SO" },
  { code: "+27", flag: "🇿🇦", label: "South Africa", iso: "ZA" },
  { code: "+82", flag: "🇰🇷", label: "South Korea", iso: "KR" },
  { code: "+34", flag: "🇪🇸", label: "Spain", iso: "ES" },
  { code: "+94", flag: "🇱🇰", label: "Sri Lanka", iso: "LK" },
  { code: "+249", flag: "🇸🇩", label: "Sudan", iso: "SD" },
  { code: "+963", flag: "🇸🇾", label: "Syria", iso: "SY" },
  { code: "+886", flag: "🇹🇼", label: "Taiwan", iso: "TW" },
  { code: "+255", flag: "🇹🇿", label: "Tanzania", iso: "TZ" },
  { code: "+66", flag: "🇹🇭", label: "Thailand", iso: "TH" },
  { code: "+216", flag: "🇹🇳", label: "Tunisia", iso: "TN" },
  { code: "+90", flag: "🇹🇷", label: "Turkey", iso: "TR" },
  { code: "+256", flag: "🇺🇬", label: "Uganda", iso: "UG" },
  { code: "+380", flag: "🇺🇦", label: "Ukraine", iso: "UA" },
  { code: "+971", flag: "🇦🇪", label: "United Arab Emirates", iso: "AE" },
  { code: "+44", flag: "🇬🇧", label: "United Kingdom", iso: "UK" },
  { code: "+598", flag: "🇺🇾", label: "Uruguay", iso: "UY" },
  { code: "+998", flag: "🇺🇿", label: "Uzbekistan", iso: "UZ" },
  { code: "+58", flag: "🇻🇪", label: "Venezuela", iso: "VE" },
  { code: "+84", flag: "🇻🇳", label: "Vietnam", iso: "VN" },
  { code: "+967", flag: "🇾🇪", label: "Yemen", iso: "YE" },
  { code: "+260", flag: "🇿🇲", label: "Zambia", iso: "ZM" },
  { code: "+263", flag: "🇿🇼", label: "Zimbabwe", iso: "ZW" }
];

// ─── Shared animation easing ─────────────────────────────────────────────────

export const ease = [0.2, 0, 0, 1] as const;
